import type { HistoryItem } from "../types";

export type PeriodGroup = {
  key: string; // "2026-W03"
  label: string; // "Week of Jan 13-19"
  items: HistoryItem[];
};

/**
 * Group history items by their period week.
 * Uses periodStart to determine the week.
 */
export function groupHistoryByPeriod(items: HistoryItem[]): PeriodGroup[] {
  const groups = new Map<string, HistoryItem[]>();

  for (const item of items) {
    const key = getWeekKeyFromDate(new Date(item.periodStart));
    const existing = groups.get(key) || [];
    groups.set(key, [...existing, item]);
  }

  // Convert to array and sort by week (descending - most recent first)
  return Array.from(groups.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, groupItems]) => ({
      key,
      label: formatWeekLabel(key),
      items: groupItems.sort(
        (a, b) =>
          new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime(),
      ),
    }));
}

/**
 * Get the Monday of a given ISO week.
 */
function getMondayOfWeek(year: number, week: number): Date {
  // Jan 4 is always in week 1
  const jan4 = new Date(year, 0, 4);
  // Get Monday of week 1
  const dayOfWeek = jan4.getDay() || 7; // Convert Sunday (0) to 7
  const mondayOfWeek1 = new Date(jan4);
  mondayOfWeek1.setDate(jan4.getDate() - dayOfWeek + 1);
  // Add weeks
  const monday = new Date(mondayOfWeek1);
  monday.setDate(mondayOfWeek1.getDate() + (week - 1) * 7);
  return monday;
}

/**
 * Get ISO week key from date.
 * Format: "2026-W03"
 */
export function getWeekKeyFromDate(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  // Get Thursday of the week (ISO 8601: weeks are defined by their Thursday)
  const dayOfWeek = d.getDay() || 7; // Convert Sunday=0 to 7
  d.setDate(d.getDate() + (4 - dayOfWeek)); // Thursday

  const year = d.getFullYear();
  const jan1 = new Date(year, 0, 1);
  const weekNum = Math.ceil(
    ((d.getTime() - jan1.getTime()) / 86400000 + 1) / 7,
  );

  return `${year}-W${weekNum.toString().padStart(2, "0")}`;
}

/**
 * Get the Monday of a given ISO week key.
 */
export function getMondayFromWeekKey(weekKey: string): Date {
  const parts = weekKey.split("-W");
  const year = Number.parseInt(parts[0] ?? "2026", 10);
  const week = Number.parseInt(parts[1] ?? "01", 10);
  return getMondayOfWeek(year, week);
}

/**
 * Navigate to previous or next week.
 */
export function getAdjacentWeek(
  weekKey: string,
  direction: "prev" | "next",
): string {
  const monday = getMondayFromWeekKey(weekKey);
  const offset = direction === "prev" ? -7 : 7;
  monday.setDate(monday.getDate() + offset);
  return getWeekKeyFromDate(monday);
}

/**
 * Format week key to human-readable label.
 * "2026-W03" -> "Week of Jan 13-19"
 */
export function formatWeekLabel(weekKey: string): string {
  const parts = weekKey.split("-W");
  const yearStr = parts[0] ?? "2026";
  const weekStr = parts[1] ?? "01";
  const year = Number.parseInt(yearStr, 10);
  const week = Number.parseInt(weekStr, 10);

  const monday = getMondayOfWeek(year, week);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const startMonth = monday.toLocaleDateString("en-US", { month: "short" });
  const endMonth = sunday.toLocaleDateString("en-US", { month: "short" });
  const startDay = monday.getDate();
  const endDay = sunday.getDate();

  if (startMonth === endMonth) {
    return `Week of ${startMonth} ${startDay}-${endDay}`;
  }
  return `Week of ${startMonth} ${startDay} - ${endMonth} ${endDay}`;
}
